import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInDays } from "date-fns";
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Typography, Box, Button, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,     
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from "state/axios";
import { 
    fetchLegMembersByFlt,
    fetchGroupedRosterDetailsByCrewCode, 
} from "state/api";
import { 
    getFlagUrl, 
    getDateFromDateTime,
    convertTimeToDecimal,
    formatTZDate,
} from "state/helpers";
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";
import FlexBetween from 'components/FlexBetween'; 
import CrewListDialog from 'components/CrewListDialog';
import CrewMemberDialog from 'components/CrewMemberDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';


const RouteCrewListDialog = ({ 
    open, 
    onClose, 
    crewRoute, 
    crewList, 
    isCrewVisible = true 
}) => {
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.timemode); 
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const { startDate, endDate } = useDateRangeContext();

    const [openCrewMemberDialog, setOpenCrewMemberDialog] = useState(false);
    const [selectedCrewMember, setSelectedCrewMember] = useState(null);

    const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
    const [selectedCrewCode, setSelectedCrewCode] = useState(null);
    const [selectedCrewName, setSelectedCrewName] = useState(null);
    const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);

    const [dialogState, setDialogState] = useState({
        openCrewList: false,
        flight: [],
        crewList: [],
        jumpList: [],
    });

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";   
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
        
    
    const fetchCrewMember = useCallback(async (crewCode) => {
        try {
            const url = `/crewmembers/read/${crewCode}`;                    
            const res = await api.get(url);

            const crewMember = {
                ...res.data,
                contactTel: res.data.contactTel || res.data.contactCell || '',
            };
            
            setSelectedCrewMember(crewMember);
        } catch (error) {
            toast.error(error?.message || "Failed to fetch crew member!");
        } 
    }, []);         

    const handleCrewMemberClick = useCallback((crewCode) => {
        fetchCrewMember(crewCode);
        setOpenCrewMemberDialog(true);
    }, [fetchCrewMember])
    
    const handleRosterDetailsClick = useCallback(async (param) => {
        try {
            const crewCode = param.crewCode;
            const crewName = param.crewName;
            setSelectedCrewCode(crewCode);
            setSelectedCrewName(crewName);

            const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
            
            setGroupedCrewRosters(result);
            setOpenRosterDetailsDialog(true);
        } catch (error) {
            setOpenRosterDetailsDialog(false);
            toast.error(error?.message || "Failed to fetch roster details.");
        } 
    }, [startDate, endDate]); 

    const handleCloseRosterDetailsDialog = () => {
        setGroupedCrewRosters([]);
        setOpenRosterDetailsDialog(false);
    };
    
    const handleCrewListClick = useCallback(async (param) => {
        try {
            const {
                flightDate, 
                flightDesc,
                dep,
            } = param;

            const {
                flightDetails, 
                legMembers, 
                jumpSeaters
            } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);           
           
            setDialogState({
                openCrewList: true,
                flight: flightDetails,
                crewList: legMembers,
                jumpList: jumpSeaters,
            });
        } catch (error) {
            setDialogState(prev => ({ ...prev, openCrewList: false }));
        } 
    }, []);
    
    const handleCloseCrewListDialog = () => {
        setDialogState(prev => ({ ...prev, openCrewList: false }));
    }; 

    const calcTotalBlockHours = (routeDetails) => {   
        if (!routeDetails || routeDetails.length === 0) return 0;     
        let totalBlockHours = 0;
        
        routeDetails.forEach(f => {
            if (!f.pax) {
                totalBlockHours += f.blockHours || convertTimeToDecimal(f.blockTime) || 0;
            }
        });
        return totalBlockHours?.toFixed(2);
    };

    const getRouteDate = (route, timeMode) => {
        const fullAtd = timeMode === 'UTC' ? route.fullAtd : route.fullAtdLocalBase;
        const fullEtd = timeMode === 'UTC' ? route.fullEtd : route.fullEtdLocalBase;
        const routeDate = fullAtd || fullEtd;

        return getDateFromDateTime(routeDate);
    };

    const addDaySuffix = (time, dayDiff) => {
        if (!time) return 'N/A';
        if (dayDiff === 1) return `${time}+`;
        if (dayDiff > 1) return `${time}+${dayDiff}`;
        return time;
    };

    const getTime = (time, routeDate) => {       
        if (!time) return 'N/A';

        const date = getDateFromDateTime(time);
        if (!date || isNaN(new Date(date))) return 'N/A';

        const dayDiff = differenceInDays(date, routeDate);        
        try {
            return addDaySuffix(formatTZDate(time, 'UTC', 'HH:mm'), dayDiff);
        } catch (e) {
            return 'N/A';
        }
    };

    const routeDate = getRouteDate(crewRoute, timeMode);
    const routeDetails = crewRoute?.routeDetails?.length
        ? crewRoute.routeDetails
        : crewRoute?.flights?.length
            ? crewRoute.flights
            : [];

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '96%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween>
                    <Typography 
                        fontWeight="bold" 
                        color="primary.dark"
                    >
                        CREW ROUTE
                    </Typography>
                    <Box 
                        display="flex" 
                        alignItems="center"
                        onClick={() => dispatch(setTimeMode())}
                    >                            
                        <img
                            alt="Flag"
                            width={25}
                            height={25}                            
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>                    
            </DialogTitle>

            <DialogContent>
                {!crewRoute || Object.keys(crewRoute).length === 0 ? (
                    <Typography>No routes available</Typography>
                ) : (                    
                    <Box 
                        py={0.5} 
                        borderBottom={`1px solid ${neutralDark}`}
                    >
                        <Box py={0.5}>
                            <Typography variant="h6">
                                {routeDate ? formatTZDate(routeDate, 'UTC', "EEE dd/MM/yyyy") : "N/A"}
                            </Typography>
                               
                            {routeDetails?.length > 0 && routeDetails?.map((f, idx) => {
                                const routeDate = getRouteDate(crewRoute, timeMode)
                                const fullDT = timeMode === 'UTC' 
                                    ? (f.fullAtd || f.fullEtd || f.depDate || null) 
                                    : (f.fullAtdLocalBase || f.fullEtdLocalBase || f.depDateLocalBase || null);
                                const fullAT = timeMode === 'UTC' 
                                    ? (f.fullAta || f.fullEta || f.arrDate || null) 
                                    : (f.fullAtaLocalBase || f.fullEtaLocalBase || f.arrDateLocalBase || null);
                                
                                return (
                                    <FlexBetween key={idx}>
                                        <Typography width="28px">{f.acType}</Typography>
                                        <Typography width="68px" textAlign="center">{f.flightReg}</Typography>                                    
                                        <Typography 
                                            width="30px" 
                                            textAlign="right"                                            
                                            sx={{
                                                cursor: 'pointer', 
                                                '&:hover': { color: theme.palette.info.light }, 
                                            }}
                                            onClick={handleCrewListClick.bind(null, f)}
                                        >
                                            {f.flightNo}
                                        </Typography>
                                        <Typography width="42px">
                                            {getTime(fullDT, routeDate)}
                                        </Typography>
                                        <Typography width="28px" textAlign="center">{f.dep}</Typography>
                                        <Typography width="28px" textAlign="center">{f.arr}</Typography>
                                        <Typography width="42px">
                                            {getTime(fullAT, routeDate)}
                                        </Typography>
                                        <Typography width="8px" textAlign="center">{f.pax}</Typography>
                                    </FlexBetween>
                                )}
                            )}
                        </Box>
                        {(isCrewVisible && crewList.length > 0) && <Divider/>}

                        {isCrewVisible && crewList.length > 0 && crewList?.map((crew, idx) => {
                            return (
                                <FlexBetween key={`${idx}-${crew.crewCode}`} mt={0.5}>     
                                    <FlexBetween 
                                        gap={0.5}
                                        sx={{
                                            color: crew?.gender === 'FM' 
                                                ? theme.palette.secondary.main 
                                                : theme.palette.primary.dark,
                                        }}
                                    >
                                        <Typography 
                                            width="18px" 
                                            textAlign='right' 
                                        >
                                            {idx + 1}. 
                                        </Typography>
                                        <Typography 
                                            flex={1}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { color: theme.palette.info.light }, 
                                            }}
                                            onClick={handleCrewMemberClick.bind(null, crew.crewCode)}
                                        >
                                            {crew.crewName || 'N/A'}
                                        </Typography>
                                    </FlexBetween> 

                                    <FlexBetween gap={0.5}>
                                        <Typography 
                                            width={isMobile ? "35px" : "42px"} 
                                            textAlign="right"         
                                            sx={{
                                                cursor: 'pointer', 
                                                '&:hover': { color: theme.palette.info.light }, 
                                            }}
                                            onClick={handleRosterDetailsClick.bind(null, crew)}
                                        >
                                            {crew.crewCode || ''}
                                        </Typography>
                                        <Typography width={isMobile ? "20px" : "40px"} textAlign="center">{crew.post || ''}</Typography>
                                        <Typography width={isMobile ? "25px" : "32px"} textAlign="center">{crew.base || ''}</Typography>
                                        {crew.contactNo 
                                            ? (
                                                <Typography
                                                    width={isMobile ? "24px" : "36px"}
                                                    aria-label={`Call ${crew.contactNo}`}
                                                    title={`Call ${crew.contactNo}`}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="flex-end"
                                                    component="a"
                                                    href={`tel:${crew.contactNo.replace(/\D/g, '').trim()}`}
                                                    sx={{
                                                        textDecoration: 'none',
                                                        cursor: 'pointer',
                                                        color: 'yellow.dark', 
                                                    }}
                                                >
                                                    <PhoneIcon /> 
                                                </Typography>                                                
                                            ) : (
                                                <Typography
                                                    width={isMobile ? "24px" : "36px"}
                                                    aria-label="No contact number"
                                                    title="No contact number"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="flex-end"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        cursor: 'not-allowed',
                                                        color: 'grey', 
                                                    }}
                                                >
                                                    <PhoneDisabledIcon />
                                                </Typography>
                                            )
                                        }
                                    </FlexBetween>
                                </FlexBetween>
                            )}
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                {isCrewVisible && crewRoute.routeDetails?.length > 0 ? (
                    <Box
                        sx={{
                            display: "inline-block",
                            p: 0.5,
                            borderBottom: "1px solid",
                            borderColor: "primary.main",
                        }}
                    >
                        <Typography>
                            BH: {calcTotalBlockHours(crewRoute.routeDetails)}
                        </Typography>
                    </Box>
                ) : (
                    <span />
                )}

                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>

            {openCrewMemberDialog &&
                <CrewMemberDialog 
                    open={openCrewMemberDialog}
                    onClose={() => setOpenCrewMemberDialog(false)}
                    crewMember={selectedCrewMember}
                />
            }

            {openRosterDetailsDialog &&
                <RosterDetailsDialog 
                    open={openRosterDetailsDialog}
                    onClose={handleCloseRosterDetailsDialog}
                    crewCode={selectedCrewCode}
                    crewName={selectedCrewName}
                    crewRosters={groupedCrewRosters}
                />
            }

            {dialogState.openCrewList &&
                <CrewListDialog 
                    open={dialogState.openCrewList}
                    onClose={handleCloseCrewListDialog}
                    flight={dialogState.flight}
                    crewList={dialogState.crewList}
                    jumpList={dialogState.jumpList}
                />
            }
        </Dialog>
    );
};

export default RouteCrewListDialog;
